/**
 * The title and small line for an event on a card or row (src/lib/event-name.ts re-exports it).
 *
 * A game's catalog row can carry the competition ahead of the matchup ("UEFA Champions League:
 * Feyenoord vs. Inter"), or the competition alone while `teams` / `teamNames` hold the clubs, and
 * its `artist` is then the competition or ONE club, so a card that printed `artist ?? name` said
 * "UEFA Champions League" (or just "Real Betis") with the opponent missing. The matchup is the
 * title; the competition (and a stage like "Group C") is the small line.
 *
 * Everything that is not a game keeps today's rule: the artist, else the name.
 *
 * @typedef {{ name: string, artist?: string | null, category?: string | null, teams?: string[] | null, teamNames?: string[] | null }} NamedEvent
 * @typedef {{ title: string, sub: string | null }} CardName
 */

/**
 * Partner typos in an event's name, put right for display only (the stored name and every id stay as the partner wrote them).
 * TicketNetwork writes "2026 Formula 1: Mexican City Grand Prix" for the Mexico City Grand Prix.
 * @param {string} name
 */
export const tidyName = (name) => String(name ?? '').replace(/\bMexican City (Grand Prix|GP)\b/gi, 'Mexico City $1');

const VS = /^(?:(.+?):\s+)?(.+?)\s+(vs\.?|v\.?|@)\s+(.+)$/i;
/** a prefix that names a competition (the catalog's category is wrong for some, e.g. Theater) */
const COMPETITION =
  /\b(league|cup|championship|trophy|liga|series|qualif\w*|friendl\w*|nations|world|euro\w*|copa|bowl|playoffs?|finals?|supercup|shield|games?)\b/i;
/** a stage glued to the first club: "Pool B - South Africa" */
const STAGE = /^([^-–:]*\b(?:pool|group|round|quarter-?finals?|semi-?finals?|matchday|week|leg|stage)\b[^-–:]*?)\s+[-–]\s+(.+)$/i;

/**
 * @param {NamedEvent} e
 * @returns {CardName}
 */
export const cardName = (e) => {
  const name = tidyName((e.name ?? '').trim());
  const artist = e.artist ? tidyName(e.artist) : e.artist;
  /** @type {CardName} */
  const plain = { title: (artist ?? name) || name, sub: null };
  // the name lacks the act ("Live at BOK Center", artist Garth Brooks): the act stays the title, the name is the small line
  if (artist && name && !name.toLowerCase().includes(String(artist).trim().toLowerCase()) && e.category !== 'Sports' && !VS.test(name)) plain.sub = name;
  const sports = e.category === 'Sports';

  const m = VS.exec(name);
  if (m) {
    const prefix = m[1]?.trim() || '';
    if (!sports && !(prefix && COMPETITION.test(prefix))) return plain;
    let home = m[2].trim();
    const away = m[4].trim();
    let sub = prefix;
    const stage = STAGE.exec(home);
    if (stage) {
      sub = prefix ? `${prefix} · ${stage[1].trim()}` : stage[1].trim();
      home = stage[2].trim();
    }
    // a runaway split is not a matchup (a tour name containing "vs"): keep the old title
    if (!home || !away || home.length > 60 || away.length > 60) return plain;
    const sep = m[3] === '@' ? '@' : 'vs.';
    return { title: `${home} ${sep} ${away}`, sub: sub || null };
  }

  // the name is only the competition, the clubs are in the row
  const clubs = (e.teamNames?.length === 2 ? e.teamNames : e.teams?.length === 2 ? e.teams : null)?.map((x) => x.trim());
  if (sports && clubs && clubs[0] && clubs[1] && clubs[0] !== clubs[1]) {
    const low = name.toLowerCase();
    if (!low.includes(clubs[0].toLowerCase()) && !low.includes(clubs[1].toLowerCase())) return { title: `${clubs[0]} vs. ${clubs[1]}`, sub: name };
  }
  return plain;
};
